import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { Tpl3plService } from '../../../services/tpl3pl.service';
import { VendorService } from '../../../services/vendor.service';
import { GlobalSearchService } from '../../../services/globalsearchservice.service';
import { RawResponseModalComponent } from '../shared/raw-response-modal/raw-response-modal.component';

export interface InboundLine {
	itemNumber: string;
	qtyOrdered: number;
	itemDescription: string;
}

export interface VendorOption {
	supplierid: string;
	suppname: string;
	address1?: string;
	address2?: string;
	address3?: string;
	address4?: string;
	address5?: string;
	address6?: string;
}

@Component({
	selector: 'app-tpl3pl-inbound',
	templateUrl: './tpl3pl-inbound.component.html',
	styleUrls: ['./tpl3pl-inbound.component.scss']
})
export class Tpl3plInboundComponent implements OnInit {
	purchaseOrderNo = '';
	poLoading = false;
	orderDate: string;
	warehouse = 'CM';
	vendorName = '';
	vendorAddress1 = '';
	vendorAddress2 = '';
	vendorCity = '';
	vendorState = '';
	vendorZipCode = '';
	vendorCountry = 'US';
	vendorSearchInput = '';
	vendorOptions: VendorOption[] = [];
	vendorSearch$ = new Subject<string>();
	vendorIncludeInactive$ = new BehaviorSubject<boolean>(false);
	vendorIncludeInactive = false;
	lines: InboundLine[] = [{ itemNumber: '', qtyOrdered: 1, itemDescription: '' }];
	defaultQtyForLoad = 1;
	skuLoadSearch = '';
	loadSkusLoading = false;
	private readonly skuLoadLimit = 5000;

	receiptLookupPo = '';
	receiptLookupLoading = false;
	receiptData: any[] | null = null;
	receiptError: string | null = null;

	statusLookupPo = '';
	statusLookupLoading = false;
	submitUnsentLoading = false;
	inboundPoStatus: any | null = null;
	inboundPoHistory: any[] = [];
	inboundPoError: string | null = null;

	constructor(
		private tpl3plService: Tpl3plService,
		private vendorService: VendorService,
		private globalSearchService: GlobalSearchService,
		private dialog: MatDialog
	) {
		this.orderDate = new Date().toISOString().slice(0, 10);
	}

	ngOnInit(): void {
		this.fetchNextPo();
		combineLatest([
			this.vendorSearch$.pipe(debounceTime(200), distinctUntilChanged()),
			this.vendorIncludeInactive$,
		]).pipe(
			switchMap(([term, inc]) => this.vendorService.getVendorSearch(term || '', !!inc))
		).subscribe((results: any) => {
			this.vendorOptions = Array.isArray(results) ? results : [];
		});
	}

	onVendorIncludeInactiveChange(checked: boolean): void {
		this.vendorIncludeInactive = !!checked;
		this.vendorIncludeInactive$.next(!!checked);
	}

	fetchNextPo(): void {
		if (this.poLoading) { return; }
		this.poLoading = true;
		this.tpl3plService.getNextInboundPo().subscribe({
			next: (res) => {
				this.poLoading = false;
				if (res.success && res.purchaseOrderNo) {
					this.purchaseOrderNo = res.purchaseOrderNo;
				} else {
					this.globalSearchService.showNotification(res.message || 'Could not get next PO number', 'warning', 'bottom', 'right');
				}
			},
			error: () => {
				this.poLoading = false;
				this.globalSearchService.showNotification('Could not get next PO number', 'danger', 'bottom', 'right');
			}
		});
	}

	onVendorSearchInput(value: string): void {
		this.vendorSearch$.next(value || '');
	}

	onVendorSelected(vendor: VendorOption): void {
		if (!vendor || !vendor.supplierid) { return; }
		this.vendorName = vendor.suppname || '';
		this.vendorAddress1 = vendor.address1 || '';
		this.vendorAddress2 = vendor.address2 || '';
		this.vendorCity = vendor.address3 || '';
		this.vendorState = vendor.address4 || '';
		this.vendorZipCode = vendor.address5 || '';
		this.vendorCountry = (vendor.address6 || 'US').trim() || 'US';
		this.vendorSearchInput = vendor.supplierid + ' - ' + (vendor.suppname || '');
	}

	vendorDisplayFn(vendor: VendorOption): string {
		if (!vendor || typeof vendor !== 'object') { return ''; }
		return (vendor.supplierid || '') + ' - ' + (vendor.suppname || '');
	}

	addLine(): void {
		this.lines.push({ itemNumber: '', qtyOrdered: 1, itemDescription: '' as string });
	}

	removeLine(index: number): void {
		if (this.lines.length <= 1) { return; }
		this.lines.splice(index, 1);
	}

	loadSkusFromInventoryScope(): void {
		if (this.loadSkusLoading) { return; }
		const qty = Math.max(1, Number(this.defaultQtyForLoad) || 1);
		const search = (this.skuLoadSearch || '').trim() || undefined;
		this.loadSkusLoading = true;
		this.tpl3plService.getSkuPushList(0, this.skuLoadLimit, search).subscribe({
			next: (res) => {
				this.loadSkusLoading = false;
				const items = res.items && res.items.length > 0 ? res.items : (res.stockids || []).map((sid: string) => ({ stockid: sid, description: '' }));
				if (items.length === 0) {
					this.globalSearchService.showNotification(res.total === 0 ? 'No in-scope SKUs found' : 'Could not load SKU list', 'warning', 'bottom', 'right');
					return;
				}
				this.lines = items.map(row => ({
					itemNumber: row.stockid,
					qtyOrdered: qty,
					itemDescription: (row.description || '').trim()
				}));
				const total = res.total ?? this.lines.length;
				if (total > this.skuLoadLimit) {
					this.globalSearchService.showNotification(`Loaded first ${this.lines.length} of ${total} in-scope SKUs. Refine search to load more.`, 'info', 'bottom', 'right');
				} else {
					this.globalSearchService.showNotification(`Loaded ${this.lines.length} in-scope SKUs (same as 3PL inventory export / SKU push).`, 'success', 'bottom', 'right');
				}
			},
			error: () => {
				this.loadSkusLoading = false;
				this.globalSearchService.showNotification('Could not load in-scope SKUs', 'danger', 'bottom', 'right');
			}
		});
	}

	onDefaultQtyChange(): void {
		const q = Math.max(1, Number(this.defaultQtyForLoad) || 1);
		this.lines.forEach(l => { l.qtyOrdered = q; });
	}

	buildPayload(): { method: string; path: string; body: any } | null {
		const validLines = this.lines
			.map(l => ({
				itemNumber: (l.itemNumber || '').trim(),
				qtyOrdered: Math.max(0, Number(l.qtyOrdered) || 0),
				itemDescription: (l.itemDescription || '').trim()
			}))
			.filter(l => l.itemNumber !== '' && l.qtyOrdered > 0 && l.itemDescription !== '');
		const missingDesc = this.lines.some(l => (l.itemNumber || '').trim() !== '' && Number(l.qtyOrdered) > 0 && (l.itemDescription || '').trim() === '');
		if (missingDesc) {
			this.globalSearchService.showNotification('Description is required for every line with SKU and qty', 'warning', 'bottom', 'right');
			return null;
		}
		if (validLines.length === 0) {
			this.globalSearchService.showNotification('Add at least one line with SKU, qty and description', 'warning', 'bottom', 'right');
			return null;
		}
		if (!(this.purchaseOrderNo || '').trim()) {
			this.globalSearchService.showNotification('Get a PO number first (click refresh next to PO field)', 'warning', 'bottom', 'right');
			return null;
		}
		const body = {
			warehouse: (this.warehouse || 'CM').trim(),
			purchaseOrderNo: (this.purchaseOrderNo || '').trim(),
			orderDate: (this.orderDate || new Date().toISOString().slice(0, 10)).slice(0, 10),
			vendorName: (this.vendorName || '').trim(),
			vendorAddress1: (this.vendorAddress1 || '').trim(),
			vendorAddress2: (this.vendorAddress2 || '').trim() || undefined,
			vendorCity: (this.vendorCity || '').trim(),
			vendorState: (this.vendorState || '').trim(),
			vendorZipCode: (this.vendorZipCode || '').trim(),
			vendorCountry: (this.vendorCountry || 'US').trim(),
			inbound_item: validLines.map((l, idx) => ({
				itemLine: String(idx + 1),
				itemNumber: l.itemNumber,
				qtyOrdered: l.qtyOrdered,
				itemDescription: l.itemDescription || ''
			}))
		};
		return { method: 'POST', path: '/api/Inbound', body };
	}

	openViewRawSend(): void {
		const requestPayload = this.buildPayload();
		if (!requestPayload) { return; }
		this.dialog.open(RawResponseModalComponent, {
			width: '720px',
			maxWidth: '96vw',
			maxHeight: '90vh',
			data: {
				context: 'inbound',
				content: { request_payload: requestPayload }
			},
			panelClass: 'raw-response-dialog'
		});
	}

	openManualInboundRaw(): void {
		const requestPayload = this.buildPayload();
		if (!requestPayload) { return; }
		this.dialog.open(RawResponseModalComponent, {
			width: '720px',
			maxWidth: '96vw',
			maxHeight: '90vh',
			data: {
				context: 'inbound',
				content: { request_payload: requestPayload }
			},
			panelClass: 'raw-response-dialog'
		});
	}

	lookupReceiptByPo(): void {
		const po = (this.receiptLookupPo || '').trim();
		if (!po) {
			this.globalSearchService.showNotification('Enter a PO number', 'warning', 'bottom', 'right');
			return;
		}
		this.receiptLookupLoading = true;
		this.receiptData = null;
		this.receiptError = null;
		this.tpl3plService.getInboundReceivedByPo(po).subscribe({
			next: (res) => {
				this.receiptLookupLoading = false;
				if (res.success && res.data != null) {
					this.receiptData = Array.isArray(res.data) ? res.data : [res.data];
					if (this.receiptData.length === 0) {
						this.receiptError = 'No receipt data for this PO.';
					}
				} else {
					this.receiptError = (res as any).message || 'No data returned';
				}
			},
			error: (err) => {
				this.receiptLookupLoading = false;
				this.receiptData = null;
				this.receiptError = err?.error?.message || err?.message || 'Failed to load receipt';
			}
		});
	}

	lookupInboundStatusByPo(): void {
		const po = (this.statusLookupPo || '').trim();
		if (!po) {
			this.globalSearchService.showNotification('Enter a PO number', 'warning', 'bottom', 'right');
			return;
		}
		this.statusLookupLoading = true;
		this.inboundPoStatus = null;
		this.inboundPoHistory = [];
		this.inboundPoError = null;
		this.tpl3plService.getInboundPoStatus(po).subscribe({
			next: (res) => {
				this.statusLookupLoading = false;
				if (res.success) {
					this.inboundPoStatus = res.data || null;
					this.inboundPoHistory = Array.isArray(res.history) ? res.history : [];
					if (this.inboundPoStatus == null && this.inboundPoHistory.length === 0) {
						this.inboundPoError = 'No inbound status found for this PO.';
					}
				} else {
					this.inboundPoError = (res as any).message || 'No status returned';
				}
			},
			error: (err) => {
				this.statusLookupLoading = false;
				this.inboundPoStatus = null;
				this.inboundPoHistory = [];
				this.inboundPoError = err?.error?.message || err?.message || 'Failed to load status';
			}
		});
	}

	submitUnsentPo(): void {
		const po = (this.statusLookupPo || '').trim();
		if (!po) {
			this.globalSearchService.showNotification('Enter a PO number', 'warning', 'bottom', 'right');
			return;
		}
		this.submitUnsentLoading = true;
		this.tpl3plService.sendInboundPo(po, false).subscribe({
			next: (res) => {
				this.submitUnsentLoading = false;
				if (res.success) {
					const msg = res.skipped ? (res.message || 'Inbound already sent for this PO') : (res.message || 'Inbound submitted');
					this.globalSearchService.showNotification(msg, 'success', 'bottom', 'right');
				} else {
					this.globalSearchService.showNotification(res.message || 'Inbound submit failed', 'danger', 'bottom', 'right');
				}
				this.lookupInboundStatusByPo();
			},
			error: (err) => {
				this.submitUnsentLoading = false;
				this.globalSearchService.showNotification(err?.error?.message || err?.message || 'Inbound submit failed', 'danger', 'bottom', 'right');
			}
		});
	}

	openInboundAttemptRaw(attempt: any): void {
		const requestPayload = {
			body: attempt?.request_data || null
		};
		const content = {
			request_payload: requestPayload,
			response: attempt?.response_data || null
		};
		this.dialog.open(RawResponseModalComponent, {
			width: '720px',
			maxWidth: '96vw',
			maxHeight: '90vh',
			data: {
				context: 'inbound',
				content
			},
			panelClass: 'raw-response-dialog'
		});
	}
}
